import './Choices.scss'
import OriginalChoices from 'choices.js'
import Container from 'choices.js/src/scripts/components/container.js'

/*

  This file provides the Choices module, augmented with custom options and
  behaviors.

  Modifications:
    - The constructor is overridden to allow custom configs without emitting a
      warning in the console
    - The dropdown will be aware of a container with its overflow hidden, and
      will shrink to make sure it is fuly visible

  New config properties:
    - positionFixed: (boolean) The dropdown will be positionned using "fixed" to
      allow overflowing even if a container has its overflow hidden

  Config defaults:
    - searchEnabled: true
    - silent: true
    - position: 'auto'
    - positionFixed: false

*/

/*

  AUTO INIT
  =========

  For an easier instanciation, select tags are automatically initialized with
  Choices when they have the .make-choices class.

  ```html
    <select class="make-choices" data-otions="{ \"searchEnabled\": true }"></select>
  ```

*/

/* CUSTOM OPTIONS */

export type AugmentedOptions = Required<typeof OriginalChoices.defaults.options> & {
  /**
   * This will allow the dropdown to overflow even inside a container
   * having its overflow hidden.
   *
   * **Input types affected:** select-one, select-multiple
   *
   * @default false
   */
  positionFixed: boolean
}

const CUSTOM_CONFIGS = ['positionFixed']

/* TYPING */

export type Choice = NonNullable<typeof OriginalChoices.defaults.options.choices>[0]

export type ChoicesGroup = {
  label: string,
  id: string,
  disabled: boolean,
  choices: Choice[]
}

class AugmentedChoicesConstructor extends OriginalChoices {
  constructor (
    selectorOrElement: string | HTMLInputElement | HTMLSelectElement,
    userConfig?: Partial<AugmentedOptions>
  ) {
    if (console && console.warn) {
      const warnBackup = console.warn
      try {
        /**
         * Override the "unknown config" warning.
         * This function will remove the custom configs declare above from the
         * unknown configs list. The warning will only be emitted if other unknown
         * configs are still left.
         */
        console.warn = function (...args: unknown[]) {
          if (args[0] !== 'Unknown config option(s) passed') {
            warnBackup(...args)
          } else {
            try {
              const configs = (args[1] as string).split(', ')
              const unknownConfigs: string[] = []
              configs.forEach(config => {
                if (CUSTOM_CONFIGS.indexOf(config) === -1) {
                  unknownConfigs.push(config)
                }
              })
              if (unknownConfigs.length > 0) {
                warnBackup(args[0], unknownConfigs.join(', '))
              }
            } catch (e) {
              warnBackup(e)
            }
          }
        }
        super(selectorOrElement, userConfig)
        console.warn = warnBackup
      } catch (e) {
        console.warn = warnBackup
        throw e
      }
    } else super(selectorOrElement, userConfig)
  }
}

declare class AugmentedChoices extends AugmentedChoicesConstructor {
  declare static readonly defaults: {
    readonly options: Partial<AugmentedOptions>
    readonly templates: typeof OriginalChoices.defaults.templates
  }

  declare readonly config: AugmentedOptions

  get containerOuter (): Container
  get dropdown (): Container

  getValue (valueOnly: boolean): string | string[]
  getValue (): Choice | Choice[]
  getValue (
    ...args: Parameters<typeof OriginalChoices.prototype.getValue>
  ): (string | Choice) | (string | Choice)[]
}

export const Choices = AugmentedChoicesConstructor as unknown as typeof AugmentedChoices
export type Choices = AugmentedChoices

declare global {
  interface HTMLSelectElement {
    choices?: Choices
  }

  interface HTMLInputElement {
    choices?: Choices
  }
}

/* POSITIONING */

/**
 * Makes sure the dropdown does not touch the edge, a small margin will be left
 */
const marginFromEdge = 4

/**
 * @param element Element where to start to bubble through DOM
 * @returns The first container having its overflow hidden
 */
const getOverflow = (element: HTMLElement): HTMLElement => {
  let parent: HTMLElement | null = element
  const path: HTMLElement[] = []
  while (parent = parent.parentElement) { // eslint-disable-line no-cond-assign
    path.push(parent)
  }
  for (let i = 0; i < path.length; i++) {
    const style = window.getComputedStyle(path[i])
    if (style['overflow'] != 'visible' || style['overflow-x'] != 'visible' || style['overflow-y'] != 'visible') { // eslint-disable-line dot-notation
      return path[i]
    }
  }
  return document.body
}

const findRequiredZIndex = (reference: Element): number => {
  let zIndex = 0
  const path: Element[] = []
  let parent: Element | null = reference
  while (parent = parent.parentElement) { // eslint-disable-line no-cond-assign
    path.push(parent)
  }
  for (let i = path.length - 1; i >= 0; i--) {
    const style = window.getComputedStyle(path[i])
    if (style['position'] != 'static' && style['z-index'] != 'auto') { // eslint-disable-line dot-notation
      zIndex = parseInt(style['z-index']) + 1
      break
    }
  }
  return zIndex
}

/**
 * Handles dropdown position (top, bottom), the height, and whether it should
 * use fixed position or not.
 */
const handleDropdownPosition = function (this: HTMLSelectElement | HTMLInputElement) {
  const choices = this.choices
  if (choices) {
    const position: string = choices.config.position
    const dropdownElement = choices.dropdown.element
    const containerElement = choices.containerOuter.element
    const overflowElement = choices.config.positionFixed ? (document.getElementById('body') || document.body) : getOverflow(dropdownElement)

    this.addEventListener('hideDropdown', () => {
      dropdownElement.style.position  = ''
      dropdownElement.style.bottom    = ''
      dropdownElement.style.top       = ''
      dropdownElement.style.width     = ''
      dropdownElement.style.left      = ''
      choices.containerOuter.position = position
    }, {
      once: true
    })

    dropdownElement.style.height = ''

    if (choices.config.positionFixed) {
      const parentElement = dropdownElement.parentElement

      if (parentElement) {
        dropdownElement.style.position  = 'fixed'
        const parentRect = parentElement.getBoundingClientRect()
        dropdownElement.style.width = parentRect.width + 'px'
        dropdownElement.style.left = parentRect.x + 'px'
        dropdownElement.style.zIndex = findRequiredZIndex(parentElement)
        let isBottom = position == 'bottom'
        isBottom ||= position == 'auto' && !choices.containerOuter.isFlipped
        if (isBottom) {
          dropdownElement.style.top = (parentRect.y + parentRect.height) + 'px'
        } else {
          dropdownElement.style.bottom = (window.innerHeight - parentRect.y) + 'px'
        }
        hideOnScroll.call(this)
      }
    } else {
      const overflowRect = overflowElement == document.documentElement
        ? { top: 0, bottom: document.documentElement.scrollHeight }
        : overflowElement.getBoundingClientRect()
      const dropdownRect = dropdownElement.getBoundingClientRect()
      const containerRect = containerElement.getBoundingClientRect()

      // Takes dropdown margins into account
      const defaultSpaceTaken = choices.containerOuter.isFlipped
        ? containerRect.top - dropdownRect.top
        : dropdownRect.bottom - containerRect.bottom

      const availableSpaceBelow = overflowRect.bottom - containerRect.bottom
      const availableSpaceAbove = containerRect.top - overflowRect.top

      const getNewHeight = (availableSpace) => {
        const overflowingHeight = defaultSpaceTaken - availableSpace
        return dropdownRect.height - overflowingHeight - marginFromEdge + 'px'
      }

      if (position == 'auto') {
        if (defaultSpaceTaken > availableSpaceBelow) {
          if (defaultSpaceTaken <= availableSpaceAbove) {
            choices.containerOuter.position = 'top'
          } else {
            if (availableSpaceAbove > availableSpaceBelow) choices.containerOuter.position = 'top'
            else choices.containerOuter.position = 'bottom'
            const bestSpace = Math.max(availableSpaceBelow, availableSpaceAbove)
            dropdownElement.style.height = getNewHeight(bestSpace)
          }
          choices.containerOuter.open(0) // Refresh top/bottom position
        } else choices.containerOuter.position = 'bottom'
      } else if (position == 'bottom') {
        if (defaultSpaceTaken > availableSpaceBelow) {
          dropdownElement.style.height = getNewHeight(availableSpaceBelow)
        }
      } else if (position == 'top') {
        if (defaultSpaceTaken > availableSpaceAbove) {
          dropdownElement.style.height = getNewHeight(availableSpaceAbove)
        }
      }
    }
  }
}

/**
 * Once a parent of the element is scrolled, the dropdown wil be hidden.
 */
const hideOnScroll = function (this: HTMLSelectElement | HTMLInputElement) {
  const choices = this.choices
  if (choices) {
    const scrollProps = ['overflow', 'overflow-x', 'overflow-y']
    const scrollValues = ['auto', 'scroll']
    const scrollableParents: HTMLElement[] = []
    let bubble: HTMLElement | null = choices.containerOuter.element
    while (bubble && (bubble = bubble.parentElement)) { // eslint-disable-line no-cond-assign
      const style = window.getComputedStyle(bubble)
      if (scrollProps.map(prop => scrollValues.includes(style[prop])).includes(true)) {
        scrollableParents.push(bubble)
      }
    }

    let stoppedListening = false

    const scrollHandler = () => choices.hideDropdown()

    const listen = () => {
      scrollableParents.forEach(scrollableParent => {
        scrollableParent.addEventListener('scroll', scrollHandler)
      })
      window.addEventListener('resize', scrollHandler)
    }

    const stopListening = () => {
      stoppedListening = true
      scrollableParents.forEach(scrollableParent => {
        scrollableParent.removeEventListener('scroll', scrollHandler)
      })
      window.removeEventListener('resize', scrollHandler)
    }

    this.addEventListener('hideDropdown', () => {
      stopListening()
    }, { once: true })
    // Opening the dropdown may cause a scroll, listening is delayed to ignore it
    requestAnimationFrame(() => {
      if (!stoppedListening) listen()
    })
  }
}

/* DEFAULT OPTIONS OVERRIDE */

Choices.defaults.options.searchEnabled = false
Choices.defaults.options.silent = true
Choices.defaults.options.position = 'auto'
Choices.defaults.options.positionFixed = false
Choices.defaults.options.itemSelectText = I18n.t('admin_menu.choises.itemSelectText')
Choices.defaults.options.noChoicesText = I18n.t('admin_menu.choises.noChoicesText')
Choices.defaults.options.noResultsText = I18n.t('admin_menu.choises.noResultsText')
Choices.defaults.options.callbackOnInit = function (this: Choices) {
  if (
    this.passedElement.element.choices?.initialised &&
    this.passedElement.element.choices != this
  ) {
    console.warn('Potential duplicate Choices instance detected on select')
  }
  this.passedElement.element.choices = this as Choices

  this.passedElement.element.removeEventListener('showDropdown', handleDropdownPosition)
  this.passedElement.element.addEventListener('showDropdown', handleDropdownPosition)
}

/* AUTO INIT .make-choices */

document.querySelectorAll<HTMLSelectElement>('.make-choices').forEach(select => {
  let options
  try {
    options = JSON.parse(select.dataset.options || '{}')
  } catch (e) {
    console.error(e)
    options = {}
  }
  void new Choices(select, options)
})
